import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  MapPin, SlidersHorizontal, ChevronDown, X, Star, ArrowUpDown,
  LayoutGrid, List as ListIcon, ChevronLeft, ChevronRight, Heart,
  Wifi, Snowflake, Coffee, Waves, Car, CalendarRange,
} from "lucide-react";
import Navbar from "../../components/Header";
import { promoActive, AvantPromo, BadgePromo } from "../../components/PromoTemp"; // PROMO TEMP

const G      = "var(--k-green)";
const G_DARK = "var(--k-green-hover)";
const API    = "https://api.komorotel.com";

const TYPE_LABELS = {
  hotel:               "Hôtel",
  chambre_hote:        "Chambre d'hôte",
  appartement_studio:  "Appartement & Studio",
};

/* === NOUVEAU: icônes/labels équipements pour la vue liste ===
   Ces champs n'existent pas encore dans le modèle Hotel. Si tu ajoutes
   un champ `amenities: ["wifi", "clim", ...]` côté backend, la vue liste
   les affichera automatiquement. Sans ce champ, la ligne est simplement
   masquée (pas de fausses données affichées). */
const AMENITY_ICONS = {
  wifi:            Wifi,
  clim:            Snowflake,
  petit_dejeuner:  Coffee,
  piscine:         Waves,
  parking:         Car,
};
const AMENITY_LABELS = {
  wifi:            "Wifi",
  clim:            "Climatisation",
  petit_dejeuner:  "Petit-déjeuner",
  piscine:         "Piscine",
  parking:         "Parking",
};

/* ═══════════════════════════════════════════════════════════
   🆕 PÉRIODES — prix d'appel

   Un hébergement peut n'avoir aucun tarif à la nuit et ne se louer qu'à la
   semaine ou au mois. Il a alors minPrice à null, et seul minPriceWeekly ou
   minPriceMonthly est renseigné.

   priceInfo   : le prix à AFFICHER, avec son unité. On montre toujours le
                 prix à la nuit quand il existe : c'est le plus lisible et le
                 plus comparable d'une carte à l'autre. Sinon on retombe sur
                 le forfait, affiché tel quel — un forfait ne se divise pas.
   nightlyOf   : le même prix ramené à la nuit, uniquement pour le filtre et
                 le tri. Jamais affiché : 360 € le mois ne se vend pas 12 €
                 la nuit, et le présenter ainsi serait mensonger.
   longStayTag : le libellé du badge, ou null. Il signale qu'un tarif longue
                 durée existe, y compris quand la carte affiche un prix nuit.
   ═══════════════════════════════════════════════════════════ */
function priceInfo(h) {
  if (h.minPrice != null)        return { value: h.minPrice,        unit: "nuit" };
  if (h.minPriceWeekly != null)  return { value: h.minPriceWeekly,  unit: "semaine" };
  if (h.minPriceMonthly != null) return { value: h.minPriceMonthly, unit: "mois" };
  return null;
}

function nightlyOf(h) {
  if (h.minPrice != null)        return h.minPrice;
  if (h.minPriceWeekly != null)  return h.minPriceWeekly / 7;
  if (h.minPriceMonthly != null) return h.minPriceMonthly / 30;
  return null;
}

function longStayTag(h) {
  const hasWeekly  = h.minPriceWeekly  != null;
  const hasMonthly = h.minPriceMonthly != null;
  if (!hasWeekly && !hasMonthly) return null;

  // Pas de tarif à la nuit : le prix affiché EST le forfait, on précise
  // simplement que la durée est imposée.
  if (h.minPrice == null) {
    return hasMonthly ? "Au mois uniquement" : "À la semaine uniquement";
  }
  // Les deux existent : la carte montre le prix nuit, le badge annonce l'autre.
  if (hasMonthly && hasWeekly) return "Aussi à la semaine et au mois";
  return hasMonthly ? "Aussi au mois" : "Aussi à la semaine";
}

/* Badge longue durée — discret, posé près du prix. */
function LongStayBadge({ label, compact = false }) {
  if (!label) return null;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: "var(--k-green-soft)", color: G_DARK,
      fontSize: compact ? 10 : 11, fontWeight: 700,
      padding: compact ? "2px 7px" : "3px 9px", borderRadius: 5,
      whiteSpace: "nowrap",
    }}>
      <CalendarRange size={compact ? 11 : 12} />
      {label}
    </span>
  );
}

/* Bloc prix réutilisé par les deux vues. */
function PriceBlock({ hotel, big = false }) {
  const info = priceInfo(hotel);
  const promo = promoActive({ hotelId: hotel._id }); // PROMO TEMP
  return (
    <>
      {promo && info && <AvantPromo prix={info.value} style={{ marginRight: 8 }} />}
      <span style={{ fontSize: big ? 17 : 17, fontWeight: 800, color: "#111827" }}>
        {info ? `${info.value}€` : "—"}
      </span>
      <span style={{ fontSize: 11, color: "#9ca3af" }}>
        {info ? ` / ${info.unit}` : " / nuit"}
      </span>
    </>
  );
}


export default function HotelList() {
  const location = useLocation();
  const navigate = useNavigate();
  const params   = new URLSearchParams(location.search);

  const destination = params.get("destination") || "";
  const checkIn     = params.get("checkIn")     || "";
  const checkOut    = params.get("checkOut")    || "";
  const adults   = params.get("adults")   || "";
  const children = params.get("children") || "";
  const hasDates    = checkIn && checkOut;

  /* ── State ── */
  const [data,         setData]         = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [sortOrder,    setSortOrder]    = useState("");
  const [maxPrice,     setMaxPrice]     = useState(1000);
  const [selectedType, setSelectedType] = useState("");
  const [showFilters,  setShowFilters]  = useState(false); // mobile sheet
  const [viewMode,     setViewMode]     = useState("grid"); // === NOUVEAU: "grid" | "list" ===

  

  /* ── Fetch ── */
  useEffect(() => {
    let url = `${API}/api/hotels/search?destination=${destination}`;
    if (checkIn && checkOut) url += `&checkIn=${checkIn}&checkOut=${checkOut}`;
    if (adults)   url += `&adults=${adults}`;
    if (children) url += `&children=${children}`;

    setLoading(true);
    fetch(url)
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [location.search]);

  /* ── Filtrage + tri ──
     🆕 PÉRIODES — le filtre et le tri passent par l'équivalent par nuit.
     Un studio à 360 € le mois vaut 12 € la nuit : il doit passer un curseur
     réglé sur 50 €, pas être écarté parce que 360 dépasse la valeur. */
  const hotels = (data || [])
    .filter(h => {
      const nightly = nightlyOf(h);
      if (nightly === null || nightly <= 0) return false;
      if (nightly > maxPrice) return false;
      if (selectedType && h.type !== selectedType) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortOrder === "asc")  return nightlyOf(a) - nightlyOf(b);
      if (sortOrder === "desc") return nightlyOf(b) - nightlyOf(a);
      return 0;
    });

  const activeFilters = (selectedType ? 1 : 0) + (maxPrice < 1000 ? 1 : 0);
  const queryString = hasDates ? `?checkIn=${checkIn}&checkOut=${checkOut}&adults=${adults || 1}&children=${children || 0}` : "";

  return (
    <div style={{ minHeight: "100vh", background: "#f9fafb", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <Navbar />

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "88px 32px 48px", display: "flex", gap: 28 }}>

        {/* ── SIDEBAR DESKTOP ── */}
        <aside style={{ width: 260, flexShrink: 0, display: "none", alignSelf: "flex-start", position: "sticky", top: 88 }}
          className="sidebar-desktop">
          <FilterPanel
            maxPrice={maxPrice} setMaxPrice={setMaxPrice}
            selectedType={selectedType} setSelectedType={setSelectedType}
            onReset={() => { setMaxPrice(1000); setSelectedType(""); }}
          />
        </aside>

        {/* ── MAIN ── */}
        <main style={{ flex: 1, minWidth: 0 }}>

          {/* Header résultats */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 700, color: "#111827", marginBottom: 4 }}>
                Hôtels à <span style={{ color: G }}>{destination || "Toutes les destinations"}</span>
              </h1>
              <p style={{ fontSize: 13, color: "#6b7280" }}>
                {loading ? "Recherche en cours..." : `${hotels.length} hébergement${hotels.length > 1 ? "s" : ""} trouvé${hotels.length > 1 ? "s" : ""}`}
              </p>
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>

              {/* === NOUVEAU: toggle vue grille / liste === */}
              <div style={{ display: "flex", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 3, gap: 2 }}>
                <button onClick={() => setViewMode("grid")} aria-label="Vue grille"
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: 7, border: "none", cursor: "pointer", background: viewMode === "grid" ? G : "transparent", color: viewMode === "grid" ? "#fff" : "#6b7280", transition: "background 0.15s" }}>
                  <LayoutGrid size={15} />
                </button>
                <button onClick={() => setViewMode("list")} aria-label="Vue liste"
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: 7, border: "none", cursor: "pointer", background: viewMode === "list" ? G : "transparent", color: viewMode === "list" ? "#fff" : "#6b7280", transition: "background 0.15s" }}>
                  <ListIcon size={15} />
                </button>
              </div>

              {/* Bouton filtres mobile */}
              <button onClick={() => setShowFilters(true)}
                style={{ display: "flex", alignItems: "center", gap: 6, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "9px 14px", fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer", position: "relative" }}>
                <SlidersHorizontal size={15} />
                Filtres
                {activeFilters > 0 && (
                  <span style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", background: G, color: "#fff", fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {activeFilters}
                  </span>
                )}
              </button>

              {/* Tri */}
              <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "9px 12px" }}>
                <ArrowUpDown size={14} color="#6b7280" />
                <select value={sortOrder} onChange={e => setSortOrder(e.target.value)}
                  style={{ border: "none", outline: "none", fontSize: 13, fontWeight: 500, color: "#374151", background: "transparent", cursor: "pointer" }}>
                  <option value="">Trier par</option>
                  <option value="asc">Prix croissant</option>
                  <option value="desc">Prix décroissant</option>
                </select>
              </div>
            </div>
          </div>

          {/* ── Empty state ── */}
          {!loading && hotels.length === 0 && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 0", textAlign: "center" }}>
              <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, marginBottom: 16 }}>😕</div>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: "#111827", marginBottom: 8 }}>
                {hasDates ? "Aucun hôtel disponible" : "Aucun résultat trouvé"}
              </h2>
              <p style={{ fontSize: 14, color: "#6b7280", maxWidth: 360, lineHeight: 1.6, marginBottom: 24 }}>
                {hasDates
                  ? "Aucun hébergement n'est disponible pour ces dates. Essayez de modifier vos dates."
                  : "Aucun hébergement ne correspond à vos filtres."}
              </p>
              <button onClick={() => navigate("/")}
                style={{ background: G, color: "#fff", border: "none", borderRadius: 10, padding: "11px 24px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                Modifier ma recherche
              </button>
            </div>
          )}

          {/* === NOUVEAU: rendu conditionnel grille / liste === */}
          {viewMode === "grid" ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 }}>
              {loading
                ? [...Array(6)].map((_, i) => <SkeletonCard key={i} />)
                : hotels.map(hotel => (
                    <HotelCard key={hotel._id} hotel={hotel} queryString={queryString} />
                  ))
              }
            </div>
          ) : (
            <div>
              {loading
                ? [...Array(4)].map((_, i) => <SkeletonRow key={i} />)
                : hotels.map(hotel => (
                    <HotelListRow key={hotel._id} hotel={hotel} queryString={queryString} />
                  ))
              }
            </div>
          )}
        </main>
      </div>

      {/* ── BOTTOM SHEET FILTRES MOBILE ── */}
      {showFilters && (
        <div style={{ position: "fixed", inset: 0, zIndex: 500 }}>
          <div onClick={() => setShowFilters(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)" }} />
          <div style={{
            position: "absolute", bottom: 0, left: 0, right: 0,
            background: "#fff", borderRadius: "20px 20px 0 0",
            padding: "0 24px 32px", maxHeight: "85vh", overflowY: "auto",
          }}>
            <div style={{ width: 40, height: 4, background: "#d1d5db", borderRadius: 2, margin: "14px auto 20px" }} />
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700 }}>Filtres</h2>
              <button onClick={() => setShowFilters(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280" }}>
                <X size={20} />
              </button>
            </div>
            <FilterPanel
              maxPrice={maxPrice} setMaxPrice={setMaxPrice}
              selectedType={selectedType} setSelectedType={setSelectedType}
              onReset={() => { setMaxPrice(1000); setSelectedType(""); }}
            />
            <button onClick={() => setShowFilters(false)}
              style={{ width: "100%", marginTop: 20, background: G, color: "#fff", border: "none", borderRadius: 12, padding: "14px 0", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
              Voir les résultats ({hotels.length})
            </button>
          </div>
        </div>
      )}

      {/* CSS responsive sidebar + vue liste */}
      <style>{`
        @media (min-width: 1024px) { .sidebar-desktop { display: block !important; } }
        @media (max-width: 720px) {
          .hotel-list-row { flex-direction: column !important; align-items: stretch !important; }
          .hotel-list-photo-block { width: 100% !important; }
          .hotel-list-price {
            width: 100% !important;
            border-left: none !important;
            border-top: 1px solid #f3f4f6 !important;
            padding-left: 0 !important;
            padding-top: 16px !important;
            flex-direction: row !important;
            align-items: center !important;
            justify-content: space-between !important;
            gap: 12px !important;
          }
          .hotel-list-price > span { width: auto !important; padding-left: 20px !important; padding-right: 20px !important; }
        }
      `}</style>
    </div>
  );
}

/* ── Card hôtel (vue grille) ── */
function HotelCard({ hotel, queryString }) {
  const [hovered, setHovered] = useState(false);
  const [liked,   setLiked]   = useState(false);
  const tag = longStayTag(hotel);   // 🆕 PÉRIODES

  return (
    <a href={`/hotel/slug/${hotel.slug}${queryString}`}
      style={{ textDecoration: "none", display: "block", borderRadius: 14, overflow: "hidden", background: "#fff", border: "1px solid #f3f4f6", boxShadow: hovered ? "0 6px 24px rgba(0,0,0,0.1)" : "0 1px 4px rgba(0,0,0,0.06)", transition: "box-shadow 0.2s, transform 0.2s", transform: hovered ? "translateY(-2px)" : "none" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>

      {/* Image */}
      <div style={{ position: "relative", height: 200, overflow: "hidden" }}>
        <img src={hotel.photos?.[0]} alt={hotel.name}
          style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.4s", transform: hovered ? "scale(1.05)" : "scale(1)" }} />
        <button onClick={e => { e.preventDefault(); setLiked(!liked); }}
          style={{ position: "absolute", top: 10, right: 10, width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,0.88)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill={liked ? "#ef4444" : "none"} stroke={liked ? "#ef4444" : "#6b7280"} strokeWidth="2">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
        </button>
        {/* PROMO TEMP */}
        {promoActive({ hotelId: hotel._id }) && <BadgePromo style={{ position: "absolute", top: 10, left: 10, fontSize: 15, padding: "5px 11px", boxShadow: "0 2px 8px rgba(0,0,0,0.2)" }} />}
        {hotel.type && (
          <div style={{ position: "absolute", bottom: 10, left: 10, background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 5, backdropFilter: "blur(4px)" }}>
            {TYPE_LABELS[hotel.type] || hotel.type}
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: "14px 16px 16px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: "#111827", lineHeight: 1.3 }}>{hotel.name}</h3>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 10 }}>
          <MapPin size={12} color="#9ca3af" />
          <span style={{ fontSize: 12, color: "#9ca3af" }}>{hotel.city}</span>
        </div>
        {hotel.desc && (
          <p style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.6, marginBottom: 12, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {hotel.desc}
          </p>
        )}

        {/* 🆕 PÉRIODES — badge longue durée, au-dessus du prix pour ne pas
            bousculer l'alignement de la ligne prix / lien. */}
        {tag && <div style={{ marginBottom: 8 }}><LongStayBadge label={tag} compact /></div>}

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: "1px solid #f9fafb", paddingTop: 10 }}>
          <div>
            <PriceBlock hotel={hotel} />
          </div>
          <span style={{ fontSize: 12, fontWeight: 700, color: G }}>Voir →</span>
        </div>
      </div>
    </a>
  );
}

/* === Card hôtel — vue liste avec carrousel photo === */
function HotelListRow({ hotel, queryString }) {
  const [photoIndex, setPhotoIndex] = useState(0);
  const [liked,      setLiked]      = useState(false);
  const [hovered,    setHovered]    = useState(false);
  const tag = longStayTag(hotel);   // 🆕 PÉRIODES
  const info = priceInfo(hotel);    // 🆕 PÉRIODES

  const photos    = hotel.photos || [];
  const hasPhotos  = photos.length > 0;

  const goPrev = (e) => { e.preventDefault(); e.stopPropagation(); setPhotoIndex(i => (i - 1 + photos.length) % photos.length); };
  const goNext = (e) => { e.preventDefault(); e.stopPropagation(); setPhotoIndex(i => (i + 1) % photos.length); };
  const selectPhoto = (e, i) => { e.preventDefault(); e.stopPropagation(); setPhotoIndex(i); };

  return (
    <a href={`/hotel/slug/${hotel.slug}${queryString}`} className="hotel-list-row"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ textDecoration: "none", color: "inherit", display: "flex", alignItems: "center", gap: 20, background: "#fff", border: "1px solid #f3f4f6", borderRadius: 14, padding: 20, marginBottom: 18, boxShadow: hovered ? "0 8px 28px rgba(0,0,0,0.08)" : "0 1px 3px rgba(0,0,0,0.03)", transition: "box-shadow 0.2s, transform 0.2s", transform: hovered ? "translateY(-2px)" : "none" }}>

      {/* photo agrandie, pastilles + cœur en overlay */}
      <div className="hotel-list-photo-block" style={{ position: "relative", width: 320, flexShrink: 0 }}>
        <div style={{ height: 236, borderRadius: 12, overflow: "hidden", background: "#f3f4f6" }}>
          {hasPhotos
            ? <img src={photos[photoIndex]} alt={hotel.name} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.4s", transform: hovered ? "scale(1.04)" : "scale(1)" }} />
            : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 12 }}>Aucune photo</div>}
        </div>

        <button onClick={e => { e.preventDefault(); e.stopPropagation(); setLiked(!liked); }}
          aria-label="Sauvegarder"
          style={{ position: "absolute", top: 10, right: 10, width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,0.9)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Heart size={14} fill={liked ? "#ef4444" : "none"} stroke={liked ? "#ef4444" : "#6b7280"} />
        </button>

        {photos.length > 1 && (
          <>
            <button onClick={goPrev} aria-label="Photo précédente"
              style={{ position: "absolute", left: 10, top: "50%", marginTop: -14, width: 28, height: 28, borderRadius: "50%", background: "rgba(255,255,255,0.88)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ChevronLeft size={16} />
            </button>
            <button onClick={goNext} aria-label="Photo suivante"
              style={{ position: "absolute", right: 10, top: "50%", marginTop: -14, width: 28, height: 28, borderRadius: "50%", background: "rgba(255,255,255,0.88)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ChevronRight size={16} />
            </button>

            {/* Pastilles de pagination */}
            <div style={{ position: "absolute", bottom: 12, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 5 }}>
              {photos.map((_, i) => (
                <span key={i} onClick={e => selectPhoto(e, i)}
                  style={{ width: photoIndex === i ? 14 : 6, height: 6, borderRadius: 3, background: photoIndex === i ? "#fff" : "rgba(255,255,255,0.55)", cursor: "pointer", transition: "width 0.2s" }} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* colonne infos */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
          <h3 style={{ fontFamily: "Georgia, serif", fontWeight: 700, fontSize: 21, color: "#111827", margin: 0, lineHeight: 1.2 }}>{hotel.name}</h3>
          {hotel.type && (
            <span style={{ background: "var(--k-green-soft)", color: G_DARK, fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 5, marginTop: 5, whiteSpace: "nowrap" }}>
              {TYPE_LABELS[hotel.type] || hotel.type}
            </span>
          )}
        </div>

        {/* Note: champ optionnel hotel.rating / hotel.reviewCount */}
        {hotel.rating && (
          <div style={{ display: "flex", alignItems: "center", gap: 5, margin: "6px 0 4px" }}>
            <Star size={14} fill="#f5b301" stroke="#f5b301" />
            <span style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{hotel.rating}</span>
            {hotel.reviewCount && <span style={{ fontSize: 12, color: "#9ca3af" }}>({hotel.reviewCount} avis)</span>}
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 4, color: "#9ca3af", fontSize: 13, marginBottom: 10 }}>
          <MapPin size={14} />
          {hotel.city}
        </div>

        {hotel.desc && (
          <p style={{ fontSize: 13.5, color: "#5b6472", lineHeight: 1.65, margin: "0 0 12px", display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {hotel.desc}
          </p>
        )}

        {/* Note: champ optionnel hotel.amenities */}
        {Array.isArray(hotel.amenities) && hotel.amenities.length > 0 && (
          <div style={{ display: "flex", gap: 16, marginTop: "auto", flexWrap: "wrap" }}>
            {hotel.amenities.map(key => {
              const Icon = AMENITY_ICONS[key];
              if (!Icon) return null;
              return (
                <span key={key} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#6b7280" }}>
                  <Icon size={15} color={G} />
                  {AMENITY_LABELS[key] || key}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* colonne prix */}
      <div className="hotel-list-price" style={{ width: 170, flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 16, textAlign: "right", borderLeft: "1px solid #f3f4f6", paddingLeft: 20 }}>
        <div>
          <span style={{ fontSize: 11, color: "#9ca3af" }}>à partir de</span>
          {/* PROMO TEMP */}
          {promoActive({ hotelId: hotel._id }) && info && <div><AvantPromo prix={info.value} /></div>}
          <div style={{ fontFamily: "Georgia, serif", fontWeight: 700, fontSize: 28, color: "#111827", lineHeight: 1.2 }}>
            {info ? `${info.value}€` : "—"}
          </div>
          <span style={{ fontSize: 11, color: "#9ca3af" }}>/ {info ? info.unit : "nuit"}</span>

          {/* 🆕 PÉRIODES — badge sous le prix, où l'information est la plus utile */}
          {tag && <div style={{ marginTop: 8 }}><LongStayBadge label={tag} compact /></div>}
        </div>
        <span style={{ background: G, color: "#fff", borderRadius: 10, padding: "11px 0", fontSize: 12, fontWeight: 700, width: "100%", textAlign: "center", display: "block" }}>
          Voir les disponibilités
        </span>
      </div>
    </a>
  );
}

/* ── Panneau filtres (réutilisé desktop + mobile) ── */
function FilterPanel({ maxPrice, setMaxPrice, selectedType, setSelectedType, onReset }) {
  const types = [
    { value: "",                    label: "Tous" },
    { value: "hotel",               label: "Hôtel" },
    { value: "appartement_studio",  label: "Appartement & Studio" },
    { value: "chambre_hote",        label: "Chambre d'hôte" },
  ];

  return (
    <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #f3f4f6", padding: "20px 18px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>Filtres</h3>
        <button onClick={onReset} style={{ background: "none", border: "none", fontSize: 12, color: G, fontWeight: 600, cursor: "pointer" }}>
          Réinitialiser
        </button>
      </div>

      {/* Prix max
          🆕 PÉRIODES — la comparaison se fait sur le prix ramené à la nuit,
          y compris pour les forfaits. Le libellé reste honnête : c'est bien
          un budget par nuit que le voyageur règle ici. */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Prix max / nuit</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: G }}>{maxPrice}€</span>
        </div>
        <input type="range" min={50} max={1000} value={maxPrice}
          onChange={e => setMaxPrice(Number(e.target.value))}
          style={{ width: "100%", accentColor: G }} />
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
          <span style={{ fontSize: 11, color: "#9ca3af" }}>50€</span>
          <span style={{ fontSize: 11, color: "#9ca3af" }}>1000€</span>
        </div>
        <p style={{ fontSize: 11, color: "#9ca3af", marginTop: 8, lineHeight: 1.5 }}>
          Les tarifs à la semaine et au mois sont comparés sur leur équivalent par nuit.
        </p>
      </div>

      {/* Type */}
      <div>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 10 }}>Type</span>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {types.map(t => (
            <label key={t.value} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 13, color: "#374151" }}>
              <div onClick={() => setSelectedType(t.value)}
                style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${selectedType === t.value ? G : "#d1d5db"}`, background: selectedType === t.value ? G : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.15s" }}>
                {selectedType === t.value && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff" }} />}
              </div>
              {t.label}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Skeleton vue grille ── */
function SkeletonCard() {
  return (
    <div style={{ borderRadius: 14, overflow: "hidden", background: "#fff", border: "1px solid #f3f4f6" }}>
      <div style={{ height: 200, background: "linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.5s infinite" }} />
      <div style={{ padding: "14px 16px 16px" }}>
        <div style={{ height: 14, background: "#f3f4f6", borderRadius: 6, marginBottom: 8, width: "70%" }} />
        <div style={{ height: 12, background: "#f3f4f6", borderRadius: 6, marginBottom: 12, width: "45%" }} />
        <div style={{ height: 12, background: "#f3f4f6", borderRadius: 6, width: "90%" }} />
      </div>
      <style>{`@keyframes shimmer { to { background-position: -200% 0; } }`}</style>
    </div>
  );
}

/* === Skeleton vue liste === */
function SkeletonRow() {
  return (
    <div style={{ display: "flex", gap: 18, background: "#fff", border: "1px solid #f3f4f6", borderRadius: 14, padding: 16, marginBottom: 16 }}>
      <div style={{ width: 300, height: 190, borderRadius: 10, flexShrink: 0, background: "linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.5s infinite" }} />
      <div style={{ flex: 1 }}>
        <div style={{ height: 16, background: "#f3f4f6", borderRadius: 6, marginBottom: 10, width: "50%" }} />
        <div style={{ height: 12, background: "#f3f4f6", borderRadius: 6, marginBottom: 14, width: "30%" }} />
        <div style={{ height: 12, background: "#f3f4f6", borderRadius: 6, width: "80%" }} />
      </div>
      <div style={{ width: 160, flexShrink: 0 }} />
      <style>{`@keyframes shimmer { to { background-position: -200% 0; } }`}</style>
    </div>
  );
}